import {useState, useCallback, useMemo, FC, memo} from "react";
import {Alert, Box, IconButton, Collapse} from "@mui/material";
import atom, {useAtom, setAtom} from "utils/atom";
import Check from "@mui/icons-material/CheckCircleOutlineOutlined";
import Error from "@mui/icons-material/ErrorOutlineOutlined";
import Help from "@mui/icons-material/HelpOutlined";
import Notifications from "@mui/icons-material/NotificationsOutlined";
import Close from "@mui/icons-material/CloseOutlined";

const SUCCESS_VARIANT = "success" as const;
const ERROR_VARIANT = "error" as const;
const WARNING_VARIANT = "warning" as const;
const INFO_VARIANT = "info" as const;

type NotificationVariant =
  | typeof SUCCESS_VARIANT
  | typeof ERROR_VARIANT
  | typeof WARNING_VARIANT
  | typeof INFO_VARIANT;

type Notification = {
  id: string;
  message: string;
  variant: NotificationVariant;
  timestamp: number;
};

// Atom to store the list of notifications
const notificationsAtom = atom<Notification[]>([]);

// Helper function to generate unique IDs
const generateId = (): string =>
  `notification-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

// Helper function to add a notification
const addNotification = (
  message: string,
  variant: NotificationVariant,
): string => {
  const id = generateId();
  const notification: Notification = {
    id,
    message,
    variant,
    timestamp: Date.now(),
  };

  setAtom(notificationsAtom, currentNotifications => [
    ...currentNotifications,
    notification,
  ]);

  // Auto-remove after 5 seconds
  setTimeout(() => {
    removeNotification(id);
  }, 5000);

  return id;
};

// Helper function to remove a notification
const removeNotification = (id: string): void => {
  setAtom(notificationsAtom, currentNotifications =>
    currentNotifications.filter(n => n.id !== id),
  );
};

// Factory function to create notification handlers
const createNotificationHandler =
  (variant: NotificationVariant) => (message: string) =>
    addNotification(message, variant);

// Toast API - the main public interface
export const Toast = {
  success: createNotificationHandler(SUCCESS_VARIANT),
  error: createNotificationHandler(ERROR_VARIANT),
  warning: createNotificationHandler(WARNING_VARIANT),
  info: createNotificationHandler(INFO_VARIANT),
} as const;

const ICON_MAP: Record<Notification["variant"], FC> = {
  [SUCCESS_VARIANT]: Check,
  [ERROR_VARIANT]: Error,
  [WARNING_VARIANT]: Help,
  [INFO_VARIANT]: Notifications,
};

const NotificationAlert: FC<{
  notification: Notification;
  onClose: (id: string) => void;
}> = memo(({notification, onClose}) => {
  const [open, setOpen] = useState(true);

  const handleClose = useCallback(() => {
    setOpen(false);
    // Delay removal to allow collapse animation
    setTimeout(() => onClose(notification.id), 300);
  }, [notification.id, onClose]);

  const IconComponent = ICON_MAP[notification.variant];

  return (
    <Collapse in={open} timeout={300}>
      <Alert
        severity={notification.variant}
        icon={<IconComponent />}
        action={
          <IconButton
            size="small"
            onClick={handleClose}
            aria-label="close"
            color="inherit">
            <Close />
          </IconButton>
        }
        sx={{
          mb: 1,
          "& .MuiAlert-message": {
            width: "100%",
          },
        }}>
        {notification.message}
      </Alert>
    </Collapse>
  );
});

/**
 * Main notification system component that renders all notifications
 * positioned in the bottom right corner
 */
export const NotificationSystem: FC = memo(() => {
  const notifications = useAtom(notificationsAtom);

  // Memoize the container styles to prevent recreation on every render
  const containerSx = useMemo(
    () => ({
      position: "fixed",
      bottom: 16,
      right: 16,
      zIndex: 9999,
      maxWidth: 400,
      width: "100%",
    }),
    [],
  );

  if (notifications.length === 0) {
    return null;
  }

  return (
    <Box sx={containerSx}>
      {notifications.map(notification => (
        <NotificationAlert
          key={notification.id}
          notification={notification}
          onClose={removeNotification}
        />
      ))}
    </Box>
  );
});
